import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WifiOff, Wifi, CloudOff } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useOfflineSync } from '@/hooks/useOfflineSync';

export function OfflineIndicator() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showIndicator, setShowIndicator] = useState(false);
  const { syncQueueSize } = useOfflineSync();

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowIndicator(true);
      
      // Hide after 3 seconds when back online
      setTimeout(() => setShowIndicator(false), 3000);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowIndicator(true);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Show indicator initially if offline
    if (!navigator.onLine) {
      setShowIndicator(true);
    }

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <AnimatePresence>
      {showIndicator && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed top-4 right-4 z-50"
        >
          <Badge
            variant={isOnline ? 'default' : 'destructive'}
            className="flex items-center gap-2 px-4 py-2 shadow-lg"
          >
            {isOnline ? (
              <>
                <Wifi className="h-4 w-4 animate-pulse" />
                <span>Back Online</span>
                {syncQueueSize > 0 && (
                  <span className="text-xs">({syncQueueSize} syncing)</span>
                )}
              </>
            ) : (
              <>
                <WifiOff className="h-4 w-4" />
                <span>Offline Mode</span>
                {syncQueueSize > 0 && (
                  <span className="text-xs">({syncQueueSize} queued)</span>
                )}
              </>
            )}
          </Badge>

          {!isOnline && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-2 bg-destructive/10 border border-destructive/20 rounded-lg p-3 backdrop-blur-sm"
            >
              <div className="flex items-start gap-2 text-sm">
                <CloudOff className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium text-destructive">You're offline</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Changes will sync when connection is restored
                  </p>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
